// Ficha del lead: panel lateral en escritorio, pantalla completa con «regresar»
// en el celular. Etapa y vendedora se cambian aquí; datos, notas y timeline
// auditado vienen del detalle (GET /admin/sales/leads/{id}). Conversaciones
// vinculadas: Orión /visitor-threads por teléfono (misma fuente que «Ver recorrido»).

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Copy, History, Loader2, Mail, MessagesSquare, Phone, Route, Save, Send, Tag, UserCheck, X,
} from 'lucide-react';

import { addLeadNote, getLead, patchLead, reassignLead, type LeadDTO } from '@/modules/leads-import';
import { JourneyDrawer } from '@/modules/public-tracking/components/JourneyDrawer';
import { getVisitorThreads, type VisitorThread } from '@/lib/api';
import type { OrgUser } from '@/modules/sales-desk/api';
import { Avatar, prettyPhone } from '@/modules/whatsapp-app/components/shared';
import { WhatsAppGlyph } from '@/modules/whatsapp-app/components/WhatsAppGlyph';
import {
  auditLabel, extraFields, isClientRecord, leadCompany, leadTitle, longWhen, originRows, shortWhen, stageLabel, type StageKey,
} from '../model';
import { StageChip, StageSelector } from './parts';

type Notify = (type: 'success' | 'error' | 'info', title: string, message?: string) => void;

const CHANNEL: Record<string, string> = {
  whatsapp: 'WhatsApp', web: 'Chat del sitio', site: 'Chat del sitio', chat_web: 'Chat del sitio', instagram: 'Instagram',
  messenger: 'Messenger', voice: 'Llamada', voz: 'Llamada', email: 'Correo', telegram: 'Telegram',
};

export function LeadSheet({
  lead: initial, names, users, usersError, canReassign, waEnabled, convEnabled, isMobile, onClose, onChanged, notify,
}: {
  lead: LeadDTO;
  names: Record<string, string>;
  users: OrgUser[] | null;
  usersError: boolean;
  canReassign: boolean;
  waEnabled: boolean;
  convEnabled: boolean;
  isMobile: boolean;
  onClose: () => void;
  onChanged: (l: LeadDTO, prevStatus?: string) => void;
  notify: Notify;
}) {
  const navigate = useNavigate();
  const [lead, setLead] = useState<LeadDTO>(initial);
  const [detailState, setDetailState] = useState<'loading' | 'ok' | 'error'>('loading');
  const [threads, setThreads] = useState<VisitorThread[] | null | 'na'>(null);
  const [journey, setJourney] = useState(false);
  const [stageBusy, setStageBusy] = useState(false);

  // Formulario de datos
  const [form, setForm] = useState({ name: initial.name, email: initial.email, phone: initial.phone, notes: initial.notes });
  const [saving, setSaving] = useState(false);
  const [assignId, setAssignId] = useState(initial.assigned_to_user_id || '');
  const [assignEmail, setAssignEmail] = useState(initial.assigned_to_email || '');
  const [reassigning, setReassigning] = useState(false);
  const [note, setNote] = useState('');
  const [posting, setPosting] = useState(false);
  const headRef = useRef<HTMLHeadingElement | null>(null);

  const hydrate = (l: LeadDTO) => {
    setLead(l);
    setForm({ name: l.name, email: l.email, phone: l.phone, notes: l.notes });
    setAssignId(l.assigned_to_user_id || '');
    setAssignEmail(l.assigned_to_email || '');
  };

  useEffect(() => {
    hydrate(initial);
    setNote('');
    setDetailState('loading');
    setThreads(null);
    let alive = true;
    getLead(initial.id).then((d) => { if (alive) { hydrate(d); setDetailState('ok'); } }).catch(() => alive && setDetailState('error'));
    const phones = [initial.phone].filter(Boolean) as string[];
    if (phones.length) {
      getVisitorThreads({ phones }).then((r) => { if (alive) setThreads(r ? r.threads : 'na'); }).catch(() => alive && setThreads('na'));
    } else setThreads([]);
    window.setTimeout(() => headRef.current?.focus(), 0);
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial.id]);

  // Esc cierra la ficha (si no hay un diálogo encima: recorrido o menú).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      if (document.querySelector('[aria-label="Ficha del visitante"], [role="dialog"][data-la-modal]')) return;
      onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const dirty = form.name !== lead.name || form.email !== lead.email || form.phone !== lead.phone || form.notes !== lead.notes;
  const reassignDirty = assignId !== (lead.assigned_to_user_id || '') || assignEmail !== (lead.assigned_to_email || '');
  const entity = isClientRecord(lead) ? 'Cliente' : 'Lead';

  const apply = (upd: LeadDTO, prev?: string) => { hydrate(upd); onChanged(upd, prev); };

  const changeStage = async (s: StageKey) => {
    const prev = lead.status;
    setStageBusy(true);
    setLead((l) => ({ ...l, status: s }));
    try {
      const upd = await patchLead(lead.id, { status: s });
      apply(upd, prev);
      notify('success', `Etapa: ${stageLabel(s)}`);
    } catch (e: any) {
      setLead((l) => ({ ...l, status: prev }));
      notify('error', 'No se cambió la etapa', e?.message);
    } finally { setStageBusy(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const upd = await patchLead(lead.id, form);
      apply(upd);
      notify('success', 'Datos guardados');
    } catch (e: any) { notify('error', 'No se guardó', e?.message); } finally { setSaving(false); }
  };

  const reassign = async () => {
    setReassigning(true);
    try {
      const u = users?.find((x) => x.user_id === assignId);
      const upd = await reassignLead(lead.id, assignId, u ? (u.email || '') : assignEmail);
      apply(upd);
      notify('success', assignId ? `Asignado a ${names[assignId] || assignEmail || assignId}` : 'Quedó sin asignar');
    } catch (e: any) { notify('error', 'No se reasignó', e?.message); } finally { setReassigning(false); }
  };

  const postNote = async () => {
    if (!note.trim()) return;
    setPosting(true);
    try {
      const upd = await addLeadNote(lead.id, note.trim());
      apply(upd);
      setNote('');
      notify('success', 'Nota agregada');
    } catch (e: any) { notify('error', 'No se agregó la nota', e?.message); } finally { setPosting(false); }
  };

  const copy = async (text: string, what: string) => {
    try { await navigator.clipboard.writeText(text); notify('success', `${what} copiado`); } catch { notify('error', 'No se pudo copiar'); }
  };

  const openThread = (t: VisitorThread) => {
    if (t.channel === 'whatsapp' && waEnabled) navigate('/m/whatsapp-app/chats');
    else if (convEnabled) navigate('/m/conversaciones/todas');
  };

  const origin = useMemo(() => originRows(lead), [lead]);
  const extra = useMemo(() => extraFields(lead), [lead]);
  const events = lead.audit_events || [];

  const field = (key: 'name' | 'email' | 'phone', label: string, type = 'text') => (
    <label className="block min-w-0">
      <span className="mb-1 block text-[12px] text-muted-foreground">{label}</span>
      <input type={type} value={form[key] || ''} onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
        className="w-full rounded-lg border border-input bg-background px-3 py-2 text-[13.5px] text-foreground" />
    </label>
  );

  const quick = (href: string | null, icon: ReactNode, label: string, onClick?: () => void) => {
    const cls = 'inline-flex min-w-0 flex-1 flex-col items-center gap-1 rounded-xl border border-border bg-card px-2 py-2 text-[12px] font-medium text-foreground hover:bg-muted disabled:opacity-50';
    if (onClick) return <button type="button" onClick={onClick} className={cls}>{icon}<span className="truncate">{label}</span></button>;
    if (!href) return <span aria-disabled className={`${cls} cursor-not-allowed opacity-50`}>{icon}<span className="truncate">{label}</span></span>;
    return <a href={href} className={cls}>{icon}<span className="truncate">{label}</span></a>;
  };

  return (
    <aside aria-label={`Ficha de ${leadTitle(lead)}`}
      className={`la-sheet-in flex h-full min-h-0 flex-col bg-card ${isMobile ? '' : 'w-[420px] shrink-0 border-l border-border xl:w-[460px]'}`}>
      {/* Encabezado */}
      <header className="flex items-start gap-3 border-b border-border px-4 py-3">
        {isMobile && (
          <button type="button" onClick={onClose} aria-label="Regresar a la lista"
            className="-ml-1 grid h-10 w-10 shrink-0 place-items-center rounded-xl text-foreground hover:bg-muted"><ArrowLeft className="h-5 w-5" /></button>
        )}
        <Avatar name={leadTitle(lead)} size={44} />
        <div className="min-w-0 flex-1">
          <h2 ref={headRef} tabIndex={-1} className="truncate text-[16px] font-semibold leading-tight text-foreground outline-none">{leadTitle(lead)}</h2>
          <p className="truncate text-[12.5px] text-muted-foreground">{leadCompany(lead) || `${entity} · llegó ${shortWhen(lead.created_at).toLowerCase()}`}</p>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <StageChip status={lead.status} />
            {entity === 'Cliente' && <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">Cliente importado</span>}
          </div>
        </div>
        {!isMobile && (
          <button type="button" onClick={onClose} aria-label="Cerrar ficha"
            className="grid h-9 w-9 shrink-0 place-items-center rounded-xl text-muted-foreground hover:bg-muted hover:text-foreground"><X className="h-5 w-5" /></button>
        )}
      </header>

      <div className="la-scroll min-h-0 flex-1 space-y-5 overflow-y-auto px-4 py-4">
        {/* Acciones rápidas */}
        <div className="flex gap-2">
          {quick(lead.phone ? `tel:${lead.phone.replace(/[^\d+]/g, '')}` : null, <Phone className="h-4 w-4" aria-hidden />, 'Llamar')}
          {quick(lead.email ? `mailto:${lead.email}` : null, <Mail className="h-4 w-4" aria-hidden />, 'Correo')}
          {waEnabled && quick(null, <WhatsAppGlyph size={16} title={null} />, 'WhatsApp', () => navigate('/m/whatsapp-app/chats'))}
          {quick(null, <Route className="h-4 w-4 la-accent-text" aria-hidden />, 'Ver recorrido', () => setJourney(true))}
        </div>

        <Section title="Etapa">
          <StageSelector value={lead.status} onChange={changeStage} busy={stageBusy} disabled={stageBusy} />
        </Section>

        <Section title="Vendedora asignada">
          {!canReassign ? (
            <p className="text-[13px] text-foreground">{names[lead.assigned_to_user_id] || lead.assigned_to_email || 'Sin asignar'}
              <span className="block text-[12px] text-muted-foreground">Solo administración reasigna leads.</span></p>
          ) : users && users.length > 0 && !usersError ? (
            <div className="flex gap-2">
              <select value={assignId} onChange={(e) => { setAssignId(e.target.value); setAssignEmail(users.find((u) => u.user_id === e.target.value)?.email || ''); }}
                aria-label="Vendedora asignada"
                className="min-w-0 flex-1 rounded-lg border border-input bg-background px-3 py-2 text-[13.5px] text-foreground">
                <option value="">Sin asignar</option>
                {lead.assigned_to_user_id && !users.some((u) => u.user_id === lead.assigned_to_user_id) && (
                  <option value={lead.assigned_to_user_id}>{lead.assigned_to_email || lead.assigned_to_user_id}</option>
                )}
                {users.filter((u) => u.is_seller).length > 0 && (
                  <optgroup label="Vendedoras">
                    {users.filter((u) => u.is_seller).map((u) => <option key={u.user_id} value={u.user_id}>{u.full_name || u.email || u.username}</option>)}
                  </optgroup>
                )}
                <optgroup label="Equipo">
                  {users.filter((u) => !u.is_seller).map((u) => <option key={u.user_id} value={u.user_id}>{u.full_name || u.email || u.username}</option>)}
                </optgroup>
              </select>
              <button type="button" onClick={reassign} disabled={!reassignDirty || reassigning}
                className="la-btn-brand inline-flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-2 text-[13px] font-semibold">
                {reassigning ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <UserCheck className="h-4 w-4" aria-hidden />}Asignar
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-[12px] text-muted-foreground">No se pudo cargar el equipo; escribe el usuario a mano.</p>
              <input value={assignId} onChange={(e) => setAssignId(e.target.value)} placeholder="user_id" aria-label="user_id de la vendedora"
                className="w-full rounded-lg border border-input bg-background px-3 py-2 text-[13.5px]" />
              <input value={assignEmail} onChange={(e) => setAssignEmail(e.target.value)} placeholder="correo (para mostrar)" aria-label="Correo de la vendedora"
                className="w-full rounded-lg border border-input bg-background px-3 py-2 text-[13.5px]" />
              <button type="button" onClick={reassign} disabled={!reassignDirty || reassigning}
                className="la-btn-brand inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-[13px] font-semibold"><UserCheck className="h-4 w-4" aria-hidden />Asignar</button>
            </div>
          )}
          {lead.assigned_at && <p className="mt-1.5 text-[11.5px] text-muted-foreground">Desde {longWhen(lead.assigned_at)}</p>}
        </Section>

        <Section title="Datos de contacto">
          <div className="grid gap-2.5">
            {field('name', 'Nombre')}
            <div className="grid gap-2.5 sm:grid-cols-2">
              {field('phone', 'Teléfono', 'tel')}
              {field('email', 'Correo', 'email')}
            </div>
            <label className="block">
              <span className="mb-1 block text-[12px] text-muted-foreground">Resumen interno</span>
              <textarea rows={2} value={form.notes || ''} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                className="w-full rounded-lg border border-input bg-background px-3 py-2 text-[13.5px]" />
            </label>
            {lead.message && (
              <div className="rounded-xl bg-muted/60 px-3 py-2 text-[13px] text-foreground">
                <span className="mb-0.5 block text-[11.5px] text-muted-foreground">Lo que escribió</span>{lead.message}
              </div>
            )}
            <div className="flex items-center gap-2">
              <button type="button" onClick={save} disabled={!dirty || saving}
                className="la-btn-brand inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-[13px] font-semibold">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Save className="h-4 w-4" aria-hidden />}Guardar
              </button>
              {dirty && <button type="button" onClick={() => hydrate(lead)} className="rounded-lg px-3 py-2 text-[13px] text-muted-foreground hover:bg-muted">Descartar</button>}
            </div>
          </div>
        </Section>

        <Section title="Conversaciones vinculadas" icon={<MessagesSquare className="h-4 w-4" aria-hidden />}>
          {threads === null ? (
            <p className="flex items-center gap-1.5 text-[12.5px] text-muted-foreground" role="status"><Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />Buscando en Orión…</p>
          ) : threads === 'na' ? (
            <p className="text-[12.5px] text-muted-foreground">No se pudieron consultar ahora. «Ver recorrido» las muestra con la trazabilidad completa.</p>
          ) : threads.length === 0 ? (
            <p className="text-[12.5px] text-muted-foreground">{lead.phone ? 'Sin conversaciones con este teléfono.' : 'Sin teléfono: no hay con qué vincular conversaciones.'}</p>
          ) : (
            <ul className="space-y-2">
              {threads.map((t) => {
                const can = (t.channel === 'whatsapp' && waEnabled) || convEnabled;
                return (
                  <li key={t.id}>
                    <button type="button" disabled={!can} onClick={() => openThread(t)}
                      className="flex w-full items-start gap-2.5 rounded-xl border border-border bg-card p-2.5 text-left hover:bg-muted/60 disabled:cursor-default disabled:hover:bg-card">
                      <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-muted">
                        {t.channel === 'whatsapp' ? <WhatsAppGlyph size={16} title={null} /> : <MessagesSquare className="h-4 w-4 text-muted-foreground" aria-hidden />}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-baseline gap-2">
                          <span className="flex-1 text-[13px] font-semibold text-foreground">{CHANNEL[t.channel] || t.channel}</span>
                          <span className="text-[11.5px] text-muted-foreground tabular-nums">{shortWhen(t.last_message_at)}</span>
                        </span>
                        <span className="line-clamp-2 text-[12.5px] text-muted-foreground">{t.last_message_preview || t.title || '—'}</span>
                        {can && <span className="mt-0.5 block text-[11.5px] la-accent-text">Abrir en {t.channel === 'whatsapp' && waEnabled ? 'WhatsApp' : 'Conversaciones'}</span>}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </Section>

        <Section title="Origen y campaña" icon={<Tag className="h-4 w-4" aria-hidden />}>
          <dl className="grid grid-cols-[minmax(0,40%)_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-[13px]">
            {origin.map(([k, v]) => (
              <div key={k} className="contents">
                <dt className="truncate text-muted-foreground">{k}</dt>
                <dd className="min-w-0 break-words text-foreground">{v}</dd>
              </div>
            ))}
          </dl>
          {lead.landing_path && (
            <button type="button" onClick={() => copy(lead.landing_path, 'Enlace de la landing')}
              className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1.5 text-[12.5px] text-foreground hover:bg-muted">
              <Copy className="h-3.5 w-3.5" aria-hidden />Copiar landing personalizada
            </button>
          )}
        </Section>

        <Section title="Notas y actividad" icon={<History className="h-4 w-4" aria-hidden />}>
          <div className="flex gap-2">
            <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} aria-label="Nueva nota"
              placeholder="Llamé, pidió cotización formal para el sábado…"
              onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); postNote(); } }}
              className="min-w-0 flex-1 rounded-lg border border-input bg-background px-3 py-2 text-[13.5px]" />
            <button type="button" onClick={postNote} disabled={!note.trim() || posting} aria-label="Agregar nota"
              className="la-btn-brand grid w-11 shrink-0 place-items-center rounded-lg">
              {posting ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Send className="h-4 w-4" aria-hidden />}
            </button>
          </div>
          {detailState === 'loading' ? (
            <p className="mt-3 flex items-center gap-1.5 text-[12.5px] text-muted-foreground" role="status"><Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />Cargando actividad…</p>
          ) : detailState === 'error' ? (
            <p className="mt-3 text-[12.5px] text-destructive">No se pudo cargar la actividad.</p>
          ) : events.length === 0 ? (
            <p className="mt-3 text-[12.5px] text-muted-foreground">Sin movimientos todavía. Cada cambio de etapa, asignación o nota queda aquí con quién y cuándo.</p>
          ) : (
            <ol className="relative ml-1.5 mt-4 space-y-3 border-l border-border">
              {events.map((ev) => (
                <li key={ev.id} className="ml-4">
                  <span className="absolute -left-[5px] mt-1.5 h-2.5 w-2.5 rounded-full border-2 border-card bg-primary" aria-hidden />
                  <div className="flex flex-wrap items-baseline gap-x-2">
                    <span className="text-[13px] font-semibold text-foreground">{auditLabel(ev.action)}</span>
                    <span className="text-[11.5px] text-muted-foreground tabular-nums">{longWhen(ev.created_at)}</span>
                  </div>
                  <div className="text-[12px] text-muted-foreground">por {ev.actor_email || (ev.actor_kind === 'agent' ? 'el agente' : ev.actor_user_id || ev.actor_kind)}</div>
                  {ev.note && <p className="mt-1 rounded-lg bg-muted/60 px-2.5 py-1.5 text-[13px] text-foreground">{ev.note}</p>}
                  {ev.diff && Object.keys(ev.diff).length > 0 && (
                    <ul className="mt-1 space-y-0.5 text-[12px]">
                      {Object.entries(ev.diff).map(([f, v]: [string, any]) => (
                        <li key={f} className="text-muted-foreground">
                          <span className="text-foreground/80">{f === 'status' ? 'Etapa' : f}</span>: <span className="line-through">{String((f === 'status' ? stageLabel(v?.before) : v?.before) ?? '∅') || '∅'}</span>
                          {' → '}<span className="font-medium text-foreground">{String((f === 'status' ? stageLabel(v?.after) : v?.after) ?? '∅') || '∅'}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ol>
          )}
        </Section>

        {extra.length > 0 && (
          <Section title="Información importada">
            <dl className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2">
              {extra.map(([k, v]) => (
                <div key={k} className="min-w-0">
                  <dt className="text-[11.5px] text-muted-foreground">{k}</dt>
                  <dd className="break-words text-[13px] text-foreground">{v}</dd>
                </div>
              ))}
            </dl>
          </Section>
        )}

        <p className="border-t border-border pt-3 text-[11.5px] text-muted-foreground">
          Llegó {longWhen(lead.created_at)} · actualizado {longWhen(lead.updated_at)}
          {lead.phone && <> · <button type="button" className="underline-offset-2 hover:underline" onClick={() => copy(lead.phone, 'Teléfono')}>{prettyPhone(lead.phone)}</button></>}
          <br /><span className="break-all">ID {lead.id}</span>
        </p>
      </div>

      {journey && <JourneyDrawer leadId={String(lead.id)} onClose={() => setJourney(false)} />}
    </aside>
  );
}

function Section({ title, icon, children }: { title: string; icon?: ReactNode; children: ReactNode }) {
  return (
    <section>
      <h3 className="mb-2 flex items-center gap-1.5 text-[13px] font-semibold text-foreground">
        {icon && <span className="text-muted-foreground">{icon}</span>}{title}
      </h3>
      {children}
    </section>
  );
}
